import {useQuery} from "@tanstack/react-query";
import {ReactNode} from "react";
import type {DefaultError, QueryKey} from "@tanstack/query-core";
import type {DefinedInitialDataOptions, UndefinedInitialDataOptions} from "@tanstack/react-query/src/queryOptions";
import type {UseQueryOptions} from "@tanstack/react-query/src/types";

export type QueryProps<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = (
  DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> |
  UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> |
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
  ) & {
  loader: ReactNode;
  error: (data: TError) => ReactNode;
  children: (data?: TData) => ReactNode;
}

export const Query = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  props: QueryProps<TQueryFnData, TError, TData, TQueryKey>
) => {
  const query = useQuery({
    ...props,
    staleTime: props.staleTime ?? 1000 * 60 * 5,
  });

  if (query.isLoading) {
    return <>{props.loader}</>;
  }

  if (query.isError) {
    return <>{props.error(query.error)}</>
  }

  return <>{props.children(query.data)}</>;
};